
import { computed, defineComponent, Ref, ref } from 'vue';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import authService from '@/b2c/shared/auth/auth.service';
import { useRouteQuery } from '@/core/browser/route-query.composable';
import { QueryKeys } from '@/shared/config/route-query.config';
import { LinkModel } from '@/types/B2CcontentServerContract';
import siteContextStore from '@/shared/content/site-context.store';
import { ActivateProfileResultStatusType, ResendRegistrationEmailStatus } from '@/b2c/shared/api/server-apis';
import siteContextService from '@/b2c/shared/site-context.service';

export default defineComponent({
    name: 'CreateProfileSuccess',
    setup() {
        const labels = useBlockLabels();
        const id = useRouteQuery<string>(QueryKeys.Id);
        const email = useRouteQuery<string>(QueryKeys.Email);
        const activationToken = useRouteQuery<string>(QueryKeys.ActivateProfileToken);
        const activationResultType: Ref<ActivateProfileResultStatusType | null> = ref(null);
        const resendError: Ref<boolean> = ref(false);
        const pending: Ref<boolean> = ref(false);

        const loginLink: Ref<LinkModel> = computed(() => {
            return siteContextStore.settings.loginLink;
        });

        const createProfileLink: Ref<LinkModel> = computed(() => {
            return siteContextService.settings?.createProfileLink ?? {} as LinkModel;
        });

        const activate = async(): Promise<void> => {
            const activation = await authService.activateProfile(activationToken.value);
            activationResultType.value = activation?.activateProfileResultStatus ?? ActivateProfileResultStatusType.InvalidToken;
        };

        const resend = async(): Promise<void> => {
            if (pending.value || !id.value) {
                return;
            }
            pending.value = true;
            try {
                const response = await authService.resendRegistrationEmail(id.value);
                const status = response?.status ?? ResendRegistrationEmailStatus.ProfileRegistrationNotFound;
                resendError.value = status !== ResendRegistrationEmailStatus.Ok;
            } finally {
                pending.value = false;
            }
        };

        const activationResultContent: Ref<string> = computed(() => {
            const results = {
                [ActivateProfileResultStatusType.InvalidToken]: labels.invalidActivationTokenContent,
                [ActivateProfileResultStatusType.ProfileRegistrationNotFound]: labels.noActivationTokenFoundContent,
                [ActivateProfileResultStatusType.TokenExpired]: labels.expiredActivationTokenContent,
                [ActivateProfileResultStatusType.Ok]: labels.activationSuccessContent,
                [ActivateProfileResultStatusType.ProfileAlreadyRegistered]: labels.userAlreadyActivatedContent,
            };

            return results[activationResultType.value];
        });

        const activationResultSuccess: Ref<boolean> = computed(() => activationResultType.value === ActivateProfileResultStatusType.Ok);

        if (activationToken.value) {
            activate();
        };

        return {
            labels,
            resend,
            activate,
            activationToken,
            pending,
            email,
            loginLink,
            createProfileLink,
            activationResultContent,
            activationResultType,
            activationResultSuccess,
            resendError,
        };
    },
});
